import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import produce from 'immer';
import clsx from 'clsx';
import { modeUIContext, setIsEditingForm, showSlideOutPanel, showSuccessNotification, SlideOutPanelLocation }
    from '@moderepo/mode-ui-state';
import { GatewayDefinition } from '@moderepo/mode-apis';
import { BaseInfoCompProps, capitalizeAll, CLAIM_CODE_MAX_LENGTH, CLAIM_CODE_MIN_LENGTH, DeviceDevInfo, DeviceDevInfoProps,
    DeviceDevInfoUpdatableInputs } from '@moderepo/mode-app-components';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById, updateProjectDevice }
    from '@moderepo/mode-data-state-ritz';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { DeviceConfigFileSettingsDialog, DeviceEthernetSettingsDialog, DeviceWifiSettingsDialog } from '../../components';
import { SendCommandSlideOutCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { DeviceCommandSettings, DeviceCommandType } from './SendDeviceCommandContainer';



interface InputErrors {
    claimCode: string | null;
}


// The device config and network config file names
const DEVICE_CONFIG_JSON_FILENAME = 'device.json';
const DEVICE_NETWORK_CONFIG_JSON_FILENAME = 'network.json';


/**
 * The set of predefined device commands that all device supports
 */
const predefinedDeviceCommands: readonly DeviceCommandSettings[] = [{
    type          : DeviceCommandType.TRADITIONAL,
    command       : 'checkAndUpdateFirmware',
    displayNameKey: 'label.send_check_and_update_firmware_command',
}, {
    type             : DeviceCommandType.TRADITIONAL,
    command          : 'rebootDevice',
    displayNameKey   : 'label.send_reboot_device_command',
    hasParameters    : true,
    defaultParameters: {
        delay: 1,
    },
}, {
    type          : DeviceCommandType.TRADITIONAL,
    command       : 'cancelRebootDevice',
    displayNameKey: 'label.cancel_reboot_device_command',
}];



/**
 * Helper function to trigger download action for the given content string and specified fileName
 */
const downloadConfigFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/octet-stream' });
    const anchor = document.createElement('A') as HTMLAnchorElement;
    anchor.download = fileName;
    anchor.setAttribute('target', 'blank');
    anchor.setAttribute('href', window.URL.createObjectURL(blob));
    anchor.click();
};



export interface DeviceDevInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly deviceId: number;
    readonly userId: number | undefined;                            // Only available if the user is viewing Device info from a user's home
    readonly homeId: number | undefined;

    // This container used DeviceDevInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;
}


export const DeviceDevInfoContainer = (props: DeviceDevInfoContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const selectedDevice = selectProjectDeviceById(dataState, props.deviceId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        claimCode: null,
    });

    const [showDeviceConfigFileSettingsDialog, setShowDeviceConfigFileSettingsDialog] = useState(false);
    const [showWifiSettingsDialog, setShowWifiSettingsDialog] = useState(false);
    const [showEthernetSettingsDialog, setShowEthernetSettingsDialog] = useState(false);

    const gatewayCatalog = useGetProjectGatewayCatalog(project);

    // find the gatewayDefinition for the selected device from the gateway catalog. If we find a gatewayDefinition, that mean the device is a
    // gateway and we should show different UI.
    const isGateway = useMemo(() => {
        if (gatewayCatalog && selectedDevice) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            }) !== undefined;
        }

        return false;
    }, [gatewayCatalog, selectedDevice]);

    /**
     * This useEffect is used for loading device data on component init
     */
    useEffect(() => {
        if (props.deviceId && !selectedDevice) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => { /* ignore error */ });
        }
    }, [dataDispatch, selectedDevice, props.projectId, props.deviceId]);

    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            claimCode: null,
        });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onDeviceDevInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);



    const onDownloadDeviceConfigFile = useCallback((gatewayTarget: string, environment: string) => {
        if (selectedDevice) {
            const content = JSON.stringify({
                gateway_target: gatewayTarget,
                environment,
                device_id     : selectedDevice.id,
                auth_key      : selectedDevice.apiKey,
            }, undefined, 4);

            downloadConfigFile(content, DEVICE_CONFIG_JSON_FILENAME);
        }
    }, [selectedDevice]);

    const onDownloadNetworkConfigWifiFile = useCallback((countryCode: string, ssid: string, password: string) => {
        const content = JSON.stringify({
            wifi: {
                country_code: countryCode,
                ssid,
                password,
            },
        }, undefined, 4);
        downloadConfigFile(content, DEVICE_NETWORK_CONFIG_JSON_FILENAME);
    }, []);

    const onDownloadNetworkConfigEthernetFile = useCallback((ipAddress: string, subnet: string, gateway?: string, nameServer?: string) => {
        const content = JSON.stringify({
            ethernet: {
                ip        : ipAddress,
                subnet,
                gateway   : gateway || undefined,
                nameserver: nameServer || undefined,
            },
        }, undefined, 4);
        downloadConfigFile(content, DEVICE_NETWORK_CONFIG_JSON_FILENAME);
    }, []);



    /**
     * On Send Command To Device option selected, we will open the Send Command To Device slide out panel
     */
    const onSendCommandToDevice = useCallback(() => {
        if (selectedDevice) {
            const options: SendCommandSlideOutCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.SEND_COMMAND_TO_DEVICE,
                compProps: {
                    data: {
                        projectId         : props.projectId,
                        deviceId          : selectedDevice.id,
                        predefinedCommands: predefinedDeviceCommands,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [props.projectId, selectedDevice, uiDispatch]);



    /**
     * Handler for when the user click Save input from the user form
     */
    const onDeviceDevInfoFormSubmit = useCallback(async (inputs: DeviceDevInfoUpdatableInputs) => {
        if (!project || !selectedDevice) {
            return;
        }

        const newInputErrors: InputErrors = {
            claimCode: null,
        };

        // get input value
        const newClaimCode = inputs.claimCode;

        if (!newClaimCode) {
            newInputErrors.claimCode = 'error.input.claim_code.empty';
        } else if (newClaimCode.length < CLAIM_CODE_MIN_LENGTH || newClaimCode.length > CLAIM_CODE_MAX_LENGTH) {
            newInputErrors.claimCode = 'error.input.claim_code.invalid';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.claimCode) {

            // Input validated
            if (newClaimCode !== selectedDevice.claimCode) {
                try {
                    // dispatch an action to update the device name and tag
                    await dataDispatch(updateProjectDevice(props.projectId, selectedDevice.id, { claimCode: newClaimCode }));

                    // Reload device
                    await dataDispatch(fetchProjectDeviceById(project.id, selectedDevice.id));

                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', {
                        term: isGateway ? 'terms.gateway' : 'terms.device',
                    }));

                    closeEditing();
                } catch (error) {
                    /* ignore error */
                }
            } else {
                // newName is the same so no need to make api call
                closeEditing();
            }
        }
    }, [dataDispatch, uiDispatch, closeEditing, selectedDevice, project, props.projectId, isGateway]);



    // Create the InputSettings for the DeviceDevInfoComp and cache it
    const inputsSettings = useMemo((): DeviceDevInfoProps['fields'] | undefined => {
        if (selectedDevice) {
            return {
                isConnected: {
                    label       : capitalizeAll(trans('label.status')),
                    displayValue: capitalizeAll(trans(selectedDevice.isConnected ? 'label.connected' : 'label.disconnected')),
                    value       : selectedDevice.isConnected,
                    hidden      : true,
                },
                connectedTime: {
                    label: capitalizeAll(trans(
                        selectedDevice.isConnected ? 'label.connected_since' : 'label.disconnected_since',
                    )),
                    displayValue: getDateTimeAndElapsedTime(
                        selectedDevice.isConnected ? selectedDevice.lastConnectTime : selectedDevice.lastDisconnectTime, trans,
                    ),
                },
                homeId: {
                    label       : capitalizeAll(trans('label.home_id')),
                    displayValue: selectedDevice.homeId ? selectedDevice.homeId.toString() : capitalizeAll(trans('label.none')),
                    value       : selectedDevice.homeId,
                },
                claimCode: {
                    label       : capitalizeAll(trans('label.claim_code')),
                    displayValue: selectedDevice.claimCode,
                    placeholder : trans('input.claim_code.placeholder'),
                    value       : selectedDevice.claimCode,
                    editable    : true,
                    error       : inputErrors.claimCode ? trans(inputErrors.claimCode) : '',
                },
                apiKey: {
                    label       : capitalizeAll(trans('label.api_key')),
                    displayValue: selectedDevice.apiKey,
                    value       : selectedDevice.apiKey,
                },
            };
        }
        return undefined;
    }, [trans, selectedDevice, inputErrors]);


    return (
        <>
            {project && selectedDevice && inputsSettings && (
                <DeviceDevInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(isGateway ? 'label.gateway_connection_info' : 'label.device_connection_info'))}
                    icon="perm_scan_wifi"
                    fields={inputsSettings}
                    actions={{
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: () => {
                                closeEditing();
                            },
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onDeviceDevInfoFormSubmit,
                        },
                    }}
                    customSecondaryActions={[{
                        label  : capitalizeAll(trans('label.edit')),
                        icon   : 'edit',
                        onClick: () => {
                            onChangeEditingMode(true);
                        },
                    }, {
                        label  : capitalizeAll(trans('label.download_device_config')),
                        icon   : 'settings',
                        onClick: () => {
                            setShowDeviceConfigFileSettingsDialog(true);
                        },
                    }, {
                        label  : capitalizeAll(trans('label.download_device_network_config')),
                        icon   : 'lan',
                        actions: [{
                            label  : capitalizeAll(trans('label.download_device_network_config_wifi')),
                            icon   : 'fas fa-wifi',
                            onClick: () => {
                                setShowWifiSettingsDialog(true);
                            },
                        }, {
                            label  : capitalizeAll(trans('label.download_device_network_config_ethernet')),
                            icon   : 'fas fa-ethernet',
                            onClick: () => {
                                setShowEthernetSettingsDialog(true);
                            },
                        }],
                    }, {
                        label  : capitalizeAll(trans('label.send_command_to_device')),
                        icon   : 'send_to_mobile',
                        onClick: onSendCommandToDevice,
                    }]}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onDeviceDevInfoFormInputChange}
                />
            )}

            {(!project || !selectedDevice) && (
                <CircularProgress />
            )}


            <DeviceConfigFileSettingsDialog
                projectId={props.projectId}
                open={showDeviceConfigFileSettingsDialog}
                onCancel={() => {
                    setShowDeviceConfigFileSettingsDialog(false);
                }}
                onSubmitDeviceConfigSettings={(gatewayTarget, environment) => {
                    onDownloadDeviceConfigFile(gatewayTarget, environment);
                    setShowDeviceConfigFileSettingsDialog(false);
                }}
            />

            <DeviceWifiSettingsDialog
                projectId={props.projectId}
                open={showWifiSettingsDialog}
                onCancel={() => {
                    setShowWifiSettingsDialog(false);
                }}
                onSubmitWifiSettings={(countryCode, ssid, password) => {
                    onDownloadNetworkConfigWifiFile(countryCode, ssid, password);
                    setShowWifiSettingsDialog(false);
                }}
            />

            <DeviceEthernetSettingsDialog
                projectId={props.projectId}
                open={showEthernetSettingsDialog}
                onCancel={() => {
                    setShowEthernetSettingsDialog(false);
                }}
                onSubmitEthernetSettings={(ipAddress, subnet, gateway, nameServer) => {
                    onDownloadNetworkConfigEthernetFile(ipAddress, subnet, gateway, nameServer);
                    setShowEthernetSettingsDialog(false);
                }}
            />
        </>
    );
};
